import { useNavigate, useSearch } from "@tanstack/react-router";
import { useEffect, useMemo, useRef, useState } from "react";

import WhiteLogo from "@/assets/white-logo.svg?react";
import { FullPageLoader } from "@/components/fullpage-loader";
import { derivePlanFromApiData } from "@/features/auth/subscription/hooks/subscriptionIntentApi.ts";
import { useGoogleTagManager } from "@/features/auth/subscription/hooks/useGoogleTagManager.ts";
import { useSubscriptionIntent } from "@/features/auth/subscription/hooks/useSubscriptionIntentHook.ts";
import { stashFirstPromoterReferralEmail } from "@/features/auth/subscription/utils/firstPromoter";
import {
  isFirstDraft,
  isFullyManaged,
} from "@/features/auth/subscription/utils/planType";
import { usePageTitle } from "@/hooks/usePageTitle";

import type { SubscriptionFormData } from "./hooks/useSubscriptionForm";

import SubscriptionForm from "./components/SubscriptionForm";
import { SubscriptionSummary } from "./components/SubscriptionSummary";

export function SubscriptionPage() {
  const navigate = useNavigate();
  const [saveDetails, setSaveDetails] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const formRef = useRef<{ submit: () => Promise<void> } | null>(null);
  const [promoCode, setPromoCode] = useState<null | string>(null);
  const [promotionCodeId, setPromotionCodeId] = useState<null | string>(null);
  const promotionRef = useRef<{
    code: null | string;
    finalAmountCents: null | number;
    id: null | string;
  } | null>(null);

  const search = useSearch({ strict: false }) as {
    promo?: string;
    promo_code?: string;
  };
  const showPromo = Number(search.promo) === 1;

  useGoogleTagManager("GTM-5X67HTS");

  useEffect(() => {
    const script = document.createElement("script");

    script.src = "https://js.stripe.com/v3/";
    script.async = true;
    document.head.append(script);

    return () => {
      script.remove();
    };
  }, []);

  const { data: apiData, error, isLoading: loading } = useSubscriptionIntent();

  const derivedPlan = useMemo(() => derivePlanFromApiData(apiData), [apiData]);

  usePageTitle(derivedPlan?.name ?? "Subscription");

  const handleFormSubmit = async (data: SubscriptionFormData) => {
    if (derivedPlan) {
      stashFirstPromoterReferralEmail(data.email);

      try {
        const purchaseData = {
          billingFrequency: derivedPlan.billingCycle,
          companyName: data.companyName || undefined,
          ownerEmail: data.email,
          ownerMobile: data.phoneNumber || undefined,
          ownerName: data.fullName,
          planPrice: `${derivedPlan.currency}${derivedPlan.price}`,
          platformName: data.companyName || undefined,
          productName: derivedPlan.name,
          promoCode: promoCode || undefined,
          purchaseType: "regular_subscription" as const,
          stripeCustomerId: data.stripePaymentMethodId || undefined,
        };

        localStorage.setItem(
          "pendingPurchaseNotification",
          JSON.stringify(purchaseData),
        );
      } catch (error) {
        console.error("Failed to save product purchase data:", error);
      }

      try {
        const successData = {
          billingCycle: derivedPlan.billingCycle,
          currency: derivedPlan.currency,
          description: derivedPlan.description,
          planName: derivedPlan.name,
          price: derivedPlan.price,
        };

        localStorage.setItem(
          "subscriptionSuccessData",
          JSON.stringify(successData),
        );
      } catch (error) {
        console.error("Failed to save subscription success data:", error);
      }
    }

    const searchParams = new URLSearchParams(window.location.search);
    const receiptEmail = searchParams.get("receipt_email");
    const resolvedCoupon = promotionRef.current?.code ?? promoCode ?? undefined;
    const chargedCents =
      promotionRef.current?.finalAmountCents ?? data.amount ?? undefined;
    const trackingParams = {
      ...(data.subscriptionId ? { sub_id: data.subscriptionId } : {}),
      ...(typeof chargedCents === "number"
        ? { value: (chargedCents / 100).toFixed(2) }
        : {}),
    };

    if (!receiptEmail) {
      navigate({
        search: {
          checkEmail: "true",
          ...(derivedPlan
            ? {
                billingCycle: derivedPlan.billingCycle,
                currency: derivedPlan.currency,
                origin: "direct-purchase",
                planName: derivedPlan.name,
                price: derivedPlan.price,
              }
            : {}),
          ...(data.invoiceId ? { inv_id: data.invoiceId } : {}),
          ...(resolvedCoupon ? { coupon: resolvedCoupon } : {}),
          ...trackingParams,
        },
        to: "/subscription-success",
      });
    } else if (derivedPlan) {
      const firstDraftPlan = isFirstDraft(derivedPlan.name);
      const fullyManagedPlan =
        !firstDraftPlan && isFullyManaged("direct-purchase", derivedPlan.name);

      if (fullyManagedPlan || firstDraftPlan) {
        navigate({
          search: {
            billingCycle: derivedPlan.billingCycle,
            currency: derivedPlan.currency,
            origin: "direct-purchase",
            planName: derivedPlan.name,
            price: derivedPlan.price,
            ...(data.invoiceId ? { inv_id: data.invoiceId } : {}),
            ...(resolvedCoupon ? { coupon: resolvedCoupon } : {}),
            ...trackingParams,
          },
          to: "/subscription-success",
        });
      } else {
        navigate({
          search: {
            billingCycle: derivedPlan.billingCycle,
            checkEmail: "true",
            currency: derivedPlan.currency,
            origin: "direct-purchase",
            planName: derivedPlan.name,
            price: derivedPlan.price,
            ...(data.invoiceId ? { inv_id: data.invoiceId } : {}),
            ...(resolvedCoupon ? { coupon: resolvedCoupon } : {}),
            ...trackingParams,
          },
          to: "/subscription-success",
        });
      }
    } else {
      navigate({ to: "/subscription-success" });
    }
  };

  const handleSummarySubmit = async () => {
    if (!termsAccepted) return;
    if (formRef.current?.submit) {
      setIsSubmitting(true);
      try {
        await formRef.current.submit();
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const handleSaveDetailsChange = (checked: boolean) => {
    setSaveDetails(checked);
  };

  return (
    <div className="min-h-screen bg-bg-secondary">
      <div className="max-w-[1200px] mx-auto px-6 py-8">
        {loading ? (
          <FullPageLoader />
        ) : error || !derivedPlan ? (
          // Error State with CTA
          <div className="bg-bg-primary rounded-2xl shadow-sm border border-border-secondary p-6 text-center">
            <p className="text-red-600 font-semibold mb-4">
              Something went wrong.
            </p>
            <p className="text-text-tertiary mb-6">
              The payment link is not working. Please contact our support team.
            </p>
            <a
              className="inline-block px-6 py-3 rounded-md bg-fg-brand-tertiary text-white font-semibold hover:opacity-90"
              href="https://nlc.com/book-a-call"
              rel="noreferrer"
              target="_blank"
            >
              Connect with NLC
            </a>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_400px] md:gap-8">
            {/* Form Section */}
            <div>
              {/* Mobile banner with plan details - only when we have API data */}
              <div
                className="md:hidden px-4 py-2 rounded-t-2xl"
                style={{
                  background:
                    "linear-gradient(45deg, #42307D 0%, #7F56D9 100%)",
                }}
              >
                <div className="flex items-center gap-2 mb-6">
                  <WhiteLogo className="w-[81px] mt-4" />
                </div>

                {/* Plan Details */}
                <div className="mb-6">
                  <div className="text-sm text-white mb-1">
                    Subscribe to {derivedPlan.name}
                  </div>
                  <div className="flex items-center gap-1">
                    <span className="text-4xl font-bold text-white">
                      {derivedPlan.currency}
                      {Number(derivedPlan.price).toLocaleString("en-US")}
                    </span>
                    <span className="text-[12px] text-white">
                      per <br /> {derivedPlan.billingCycle}
                    </span>
                  </div>
                </div>
              </div>
              <div className="bg-bg-primary rounded-b-2xl md:rounded-2xl shadow-sm border border-border-secondary p-4 xl:p-10 mb-8">
                <SubscriptionForm
                  onSubmit={handleFormSubmit}
                  onSubmittingChange={setIsSubmitting}
                  promotionCode={promoCode}
                  promotionCodeId={promotionCodeId}
                  promotionRef={promotionRef}
                  ref={formRef}
                  subscriptionApiData={apiData || undefined}
                />
              </div>
            </div>

            {/* Summary Section */}
            <div className="lg:sticky lg:top-8 lg:self-start">
              <SubscriptionSummary
                initialCoupon={search.promo_code}
                isSubmitting={isSubmitting}
                onPromoApplied={(code, promotionCodeId, finalAmountCents) => {
                  setPromoCode(code);
                  setPromotionCodeId(promotionCodeId || null);
                  // Keep an always-fresh reference for submit-time reads (avoids React render timing)
                  promotionRef.current = {
                    code,
                    finalAmountCents: finalAmountCents ?? null,
                    id: promotionCodeId || null,
                  };
                }}
                onSaveDetailsChange={handleSaveDetailsChange}
                onSubmit={handleSummarySubmit}
                onTermsChange={setTermsAccepted}
                plan={derivedPlan}
                saveDetails={saveDetails}
                showPromo={showPromo}
                termsAccepted={termsAccepted}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
